import React, { useCallback, memo, useRef, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Text, Box, xcss } from '@atlaskit/primitives';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { fg } from '@atlassian/jira-feature-gating';
import { toFlagId, useFlagsService, type FlagId } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import ViewsIllustration from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/clipboard';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	JSM_VIEWS,
	JSM_BOARD,
	JSM_CALENDAR,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants.tsx';
import { FeaturesToggleTile } from '../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../controllers/common/service-project-features-controller/index.tsx';
import ViewsFeaturesOld from './index-old';
import messages from './messages';

const VIEWS_DOCUMENTATION_URL =
	'https://support.atlassian.com/jira-service-management-cloud/docs/manage-your-work-using-a-board/';
const VIEWS_HELP_ARTICLE_ID = '103YhAB4GHpQvY8MAc0Q2r';

const ViewsFeatures = memo(() => {
	const { formatMessage } = useIntl();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});
	const { showFlag, dismissFlag } = useFlagsService();
	const [showViewsSettings, setShowViewsSettings] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onViewsToggleSuccess = useCallback(
		({ enabled }: { enabled: boolean }) => {
			const flagIdKey = 'views-service-feature-success';
			const { current: flagIds } = flagIdRef;
			flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);
			const flagId = flagIds[flagIdKey];

			const onDismiss = () => {
				dismissFlag(flagId);
			};

			// Dismiss previous flag with the same id
			dismissFlag(flagId);

			showFlag({
				id: flagId,
				type: 'success',
				title: [messages.viewsFlagSuccessTitle, { value: enabled }],
				description: enabled
					? messages.viewsEnabledSuccessFlagDescription
					: messages.viewsDisabledSuccessFlagDescription,
				actions: [
					{
						content: enabled
							? messages.viewsFeatureSuccessEnableAction
							: messages.viewsFeatureSuccessDisableAction,
						onClick: () => {
							onDismiss();
						},
					},
				],
			});
		},
		[dismissFlag, showFlag],
	);

	const onViewsToggleError = useCallback(
		({ enabled }: { enabled: boolean }) => {
			showSaveErrorFlag(formatMessage(messages.viewsTitle), enabled, {
				errorTitle: messages.enabledErrorTitleForViews,
				errorDescription: messages.saveErrorDescription,
			});
		},
		[showSaveErrorFlag, formatMessage],
	);

	const onViewsChange = async (
		features: string[],
		enabled: boolean,
		analyticEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticEvent, 'settingsToggle', { field: JSM_VIEWS });

		let hasError = false;
		await Promise.all(
			features.map(
				(feature) =>
					new Promise((resolve) => {
						update({
							feature,
							enabled,
							onFail: () => {
								hasError = true;
								resolve(null);
							},
							onSuccess: () => {
								resolve(null);
							},
						});
					}),
			),
		);

		if (hasError) {
			// Revert the changes if there was an error
			await Promise.all(
				features.map(
					(feature) =>
						new Promise((resolve) => {
							update({
								feature,
								enabled: !enabled,
								onFail: () => {
									resolve(null);
								},
								onSuccess: () => {
									resolve(null);
								},
							});
						}),
				),
			);
			onViewsToggleError({ enabled });
		} else {
			onViewsToggleSuccess({ enabled });
		}
	};

	const onViewsSettingsClick = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'openViewsSettingsModal');
		setShowViewsSettings(true);
	};

	return (
		<FeaturesToggleTile
			features={[JSM_BOARD, JSM_CALENDAR]}
			title={formatMessage(messages.viewsTitle)}
			badge={
				fg('jsm_views_of_work_new_lozenge') && (
					<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
				)
			}
			value={data?.[JSM_BOARD]?.status === ENABLED || data?.[JSM_CALENDAR]?.status === ENABLED}
			saving={saving[JSM_BOARD] || saving[JSM_CALENDAR]}
			onChange={onViewsChange}
			illustration={<ViewsIllustration alt="" />}
		>
			<FormattedMessage
				{...messages.viewsDescription}
				values={{
					b: (chunk) => <Text weight="bold">{chunk}</Text>,
					link: (chunk) => (
						<HelpPanelLink
							articleId={VIEWS_HELP_ARTICLE_ID}
							appearance="link"
							spacing="none"
							fallbackComponent={
								<Link href={VIEWS_DOCUMENTATION_URL} target="_blank">
									{chunk}
								</Link>
							}
							onClick={() =>
								fireUIAnalytics(
									createAnalyticsEvent({}),
									'helpLink clicked',
									'learnMoreAboutJsmViews',
									{
										articleId: VIEWS_HELP_ARTICLE_ID,
									},
								)
							}
						>
							{chunk}
						</HelpPanelLink>
					),
				}}
			/>
			<Box xcss={buttonWrapperStyles}>
				<Button iconBefore={SettingsIcon} onClick={onViewsSettingsClick}>
					{formatMessage(messages.selectViewsButton)}
				</Button>
				{showViewsSettings &&
					// TODO: Implement the settings modal
					/* <AsyncViewsSettings
						onClose={() => setShowViewsSettings(false)}
					/> */ null}
			</Box>
		</FeaturesToggleTile>
	);
});

const buttonWrapperStyles = xcss({
	marginTop: 'space.100',
});

export default componentWithFG('jsm_views_of_work_phase_1', ViewsFeatures, ViewsFeaturesOld);
